// VaadMembershipsSection.tsx
import React, { useState } from "react";
import {
  Row,
  Col,
  Typography,
  Button,
  Card,
  Tag,
  Space,
  Select,
  DatePicker,
  Badge,
  InputNumber,
  message,
  Tooltip,
} from "antd";
import { PlusOutlined, ExclamationCircleFilled } from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";
import { useCancelSubscription } from "../../../../../hooks/api/users";
import { useSimpleModal } from "../../../../Common/Modal/Simple";
import {
  useMessageError,
  useMessageSuccess,
} from "../../../../../hooks/common";

export interface VaadMembership {
  id: string;
  vaadId: string;
  vaadUuid?: string;
  vaadName: string;
  vaadStatus: "started" | "not_started" | "active" | "inactive";
  subscriptionStatus?:
  | "active"
  | "trialing"
  | "incomplete"
  | "incomplete_expired"
  | "past_due"
  | "canceled"
  | "unpaid"
  | "paused"
  | "payment_failed"
  | null;
  userStatus:
  | "new_lead"
  | "form_sent"
  | "confirmed"
  | "invited"
  | "active"
  | "inactive"
  | "archived";
  discountValue: number;
  discountEndDate?: Dayjs | null;
  numberOfMonths?: number | null;
  stripeManageUrl?: string;
  subscriptionTill?: Dayjs | null;
  stripeSubscriptionId?: string | null;
  subscriptionCanceledAt?: Dayjs | null;
}

const discountOptions = Array.from({ length: 101 }, (_, value) => ({
  value,
  label: `${value}%`,
}));

const userStatusOptions = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
  { value: "new_lead", label: "New Lead" },
  { value: "archived", label: "Archived" },
];

const formatUserStatusLabel = (status: string): string =>
  status
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

const getUserStatusOptions = (currentStatus?: string) => {
  if (
    !currentStatus ||
    userStatusOptions.some((option) => option.value === currentStatus)
  ) {
    return userStatusOptions;
  }

  return [
    ...userStatusOptions,
    { value: currentStatus, label: formatUserStatusLabel(currentStatus) },
  ];
};

export interface VaadMembershipsSectionProps {
  memberships: VaadMembership[];
  availableVaads: { id: string; name: string }[];
  onMembershipsChange: (memberships: VaadMembership[]) => void;
  onSubscriptionCanceled?: () => void | Promise<unknown>;
}

export default function VaadMembershipsSection({
  memberships,
  availableVaads,
  onMembershipsChange,
  onSubscriptionCanceled,
}: VaadMembershipsSectionProps): React.ReactElement {
  const [showAddForm, setShowAddForm] = useState(false);
  const [newMembership, setNewMembership] = useState<Partial<VaadMembership>>({
    userStatus: "new_lead",
    discountValue: 50,
  });
  const cancelSubscription = useCancelSubscription();
  const { open, contextHolder } = useSimpleModal();

  const handleCancelSubscription = (membership: VaadMembership) => {
    open({
      title: "Cancel subscription?",
      icon: <ExclamationCircleFilled style={{ color: "#FAAD14" }} />,
      content:
        "Your subscription will be cancelled. Are you sure you want to continue?",
      okText: "Yes, cancel",
      okButtonProps: { danger: true },
      cancelText: "No",
      onOk: async () => {
        try {
          await cancelSubscription.fetch(
            undefined,
            `memberships/${membership.id}/cancel-subscription`,
          );
          // message.success("The subscription has been canceled successfully");
          // Refetch from the backend so the canceled state (and its date) is
          // reflected immediately, without setting anything locally.
          await onSubscriptionCanceled?.();
        } catch (error) {
          // message.error("Failed to cancel the subscription. Try again.");
          throw error;
        }
      },
    });
  };
  useMessageSuccess(
    [cancelSubscription],
    "The subscription has been canceled successfully",
  );
  useMessageError([cancelSubscription]);
  const handleUserStatusChange = (
    id: string,
    userStatus: VaadMembership["userStatus"],
  ) => {
    const updated = memberships.map((m) =>
      m.id === id ? { ...m, userStatus } : m,
    );
    onMembershipsChange(updated);
  };

  const handleSubscriptionStatusChange = (
    id: string,
    subscriptionStatus: VaadMembership["subscriptionStatus"],
  ) => {
    const updated = memberships.map((m) =>
      m.id === id ? { ...m, subscriptionStatus } : m,
    );
    onMembershipsChange(updated);
  };

  const handleDiscountChange = (id: string, discountValue: number) => {
    const updated = memberships.map((m) =>
      m.id === id ? { ...m, discountValue } : m,
    );
    onMembershipsChange(updated);
  };

  const handleNumberOfMonthsChange = (
    id: string,
    numberOfMonths: number | null,
  ) => {
    const updated = memberships.map((m) =>
      m.id === id ? { ...m, numberOfMonths } : m,
    );
    onMembershipsChange(updated);
  };

  const handleSubscriptionTillChange = (
    id: string,
    subscriptionTill: Dayjs | null,
  ) => {
    const updated = memberships.map((m) =>
      m.id === id ? { ...m, subscriptionTill } : m,
    );
    onMembershipsChange(updated);
  };

  const handleAddMembership = () => {
    if (!newMembership.vaadId) return;

    const vaad = availableVaads.find((v) => v.id === newMembership.vaadId);
    if (!vaad) return;

    const membership: VaadMembership = {
      id: `vaad-${Date.now()}`,
      vaadId: vaad.id,
      vaadUuid: vaad.id,
      vaadName: vaad.name,
      vaadStatus: "not_started",
      userStatus: newMembership.userStatus as VaadMembership["userStatus"],
      discountValue: newMembership.discountValue || 50,
      discountEndDate: newMembership.discountEndDate,
      numberOfMonths: newMembership.numberOfMonths ?? null,
    };

    onMembershipsChange([...memberships, membership]);
    setShowAddForm(false);
    setNewMembership({ userStatus: "new_lead", discountValue: 50 });
  };

  const handleCancelAdd = () => {
    setShowAddForm(false);
    setNewMembership({ userStatus: "new_lead", discountValue: 50 });
  };

  const availableVaadsToAdd = availableVaads.filter(
    (v) => !memberships.some((m) => m.vaadId === v.id),
  );

  return (
    <div>
      {contextHolder}
      <Row
        justify="space-between"
        align="middle"
        style={{ marginBottom: 16, padding: "0 16px" }}
      >
        <Typography.Text strong style={{ fontSize: 16 }}>
          Vaad Memberships
        </Typography.Text>
        <Typography.Text type="secondary">
          {memberships.length} active Vaad{memberships.length !== 1 ? "s" : ""}
        </Typography.Text>
      </Row>
      {memberships.map((membership) => (
        <Card
          key={membership.id}
          style={{
            marginBottom: 12,
            borderRadius: 8,
            border: "1px solid #f0f0f0",
          }}
          bodyStyle={{ padding: 16 }}
        >
          <Row justify="space-between" align="middle">
            <Col>
              <Typography.Text strong style={{ fontSize: 15 }}>
                {membership.vaadName}
              </Typography.Text>
              <Tag
                variant="outlined"
                style={{ marginLeft: 24 }}
                color={
                  membership.vaadStatus === "started" ||
                    membership.vaadStatus === "active"
                    ? "green"
                    : membership.vaadStatus === "inactive"
                      ? "red"
                      : "orange"
                }
              >
                {membership.vaadStatus === "started"
                  ? "Started"
                  : membership.vaadStatus === "active"
                    ? "Active"
                    : membership.vaadStatus === "inactive"
                      ? "Inactive"
                      : "Not Started"}
              </Tag>
            </Col>
          </Row>

          <Row gutter={24} style={{ marginTop: 16 }} align="middle">
            <Col>
              <Space align="center">
                <span>Subscription Status</span>
                <span className="capitalize-first-letter">
                  {membership.subscriptionStatus || "-"}
                </span>
                {/* <Select
                  disabled
                  value={membership.subscriptionStatus}
                  onChange={(value) =>
                    handleSubscriptionStatusChange(membership.id, value)
                  }
                  style={{ width: 140 }}
                  options={[
                    { value: "active", label: "Active" },
                    { value: "failed", label: "Payment failed" },
                    { value: "cancelled", label: "Cancelled" },
                  ]}
                /> */}
              </Space>
            </Col>
            <Col>
              <Space align="center">
                <span>User Status</span>
                <Select
                  value={membership.userStatus}
                  onChange={(value) =>
                    handleUserStatusChange(membership.id, value)
                  }
                  style={{ width: 140 }}
                  options={getUserStatusOptions(membership.userStatus)}
                />
              </Space>
            </Col>
          </Row>

          <Row gutter={24} style={{ marginTop: 16 }} align="middle">
            <Col>
              <Space align="center">
                <span>Discount</span>
                <Select
                  value={membership.discountValue}
                  onChange={(value) =>
                    handleDiscountChange(membership.id, value)
                  }
                  style={{ width: 140 }}
                  showSearch
                  optionFilterProp="label"
                  listHeight={256}
                  options={discountOptions}
                />
              </Space>
            </Col>
            {/* <Col>
              <Space align="center">
                <span>
                  Discount End Date:{" "}
                  {membership.discountEndDate
                    ? membership.discountEndDate.format("MMM DD, YYYY")
                    : "-"}
                </span>
              </Space>
            </Col> */}
            <Col>
              <Space align="center">
                <span>Discount duration</span>
                <InputNumber
                  min={0}
                  value={membership.numberOfMonths ?? undefined}
                  onChange={(value) =>
                    handleNumberOfMonthsChange(membership.id, value)
                  }
                  style={{ width: 100 }}
                  placeholder="Months"
                />
              </Space>
            </Col>
            <Col>
              <Space align="center">
                <span>Subscription till</span>
                <DatePicker
                  value={membership.subscriptionTill || null}
                  disabled={!!membership.stripeSubscriptionId}
                  disabledDate={(current) =>
                    current && current < dayjs().startOf("day")
                  }
                  onChange={(date) =>
                    handleSubscriptionTillChange(membership.id, date)
                  }
                  style={{ width: 160 }}
                  format="MMM DD, YYYY"
                  placeholder="Select date"
                />
              </Space>
            </Col>
            <Col flex="auto" style={{ textAlign: "right" }}>
              <Space>
                {membership.subscriptionCanceledAt ? (
                  <Typography.Text type="secondary">
                    Canceled on{" "}
                    {membership.subscriptionCanceledAt.format("MMM DD, YYYY")}
                  </Typography.Text>
                ) : (
                  !membership.id.startsWith("vaad-") &&
                  membership.subscriptionStatus &&
                  membership.subscriptionStatus !== "canceled" && (
                    <Button
                      type="text"
                      danger
                      onClick={() => handleCancelSubscription(membership)}
                    >
                      Cancel subscription
                    </Button>
                  )
                )}
                <Tooltip
                  title={
                    membership?.stripeSubscriptionId
                      ? undefined
                      : "User has no active Stripe subscription"
                  }
                >
                  <Button
                    type="primary"
                    ghost
                    disabled={!membership?.stripeSubscriptionId}
                    onClick={() => {
                      if (membership?.stripeSubscriptionId) {
                        window.open(
                          `https://dashboard.stripe.com/subscriptions/${membership.stripeSubscriptionId}`,
                          "_blank",
                          "noopener,noreferrer",
                        );
                      }
                    }}
                  >
                    Manage in Stripe
                  </Button>
                </Tooltip>
              </Space>
            </Col>
          </Row>
        </Card>
      ))}
      {!showAddForm && availableVaadsToAdd.length > 0 && (
        <Button
          type="dashed"
          block
          icon={<PlusOutlined />}
          onClick={() => setShowAddForm(true)}
        >
          Add to another Vaad
        </Button>
      )}

      {showAddForm && (
        <Card
          style={{
            borderRadius: 8,
            border: "1px solid #1890ff",
            marginTop: 8,
          }}
          bodyStyle={{ padding: 20 }}
        >
          <Row style={{ marginBottom: 16 }}>
            <Col>
              <Space align="center">
                <Typography.Text>User status:</Typography.Text>
                <Badge color="#1677FF" />
                <Typography.Text>New Lead</Typography.Text>
              </Space>
            </Col>
          </Row>

          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col span={24}>
              <div style={{ marginBottom: 8, color: "#666" }}>Select Vaad</div>
              <Select
                placeholder="Select one"
                style={{ width: "100%" }}
                value={newMembership.vaadId}
                onChange={(value) =>
                  setNewMembership({ ...newMembership, vaadId: value })
                }
                options={availableVaadsToAdd.map((v) => ({
                  value: v.id,
                  label: v.name,
                }))}
              />
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <div style={{ marginBottom: 8, color: "#666" }}>
                Select discount
              </div>
              <Select
                placeholder="Select one"
                style={{ width: "100%" }}
                value={newMembership.discountValue}
                onChange={(value) =>
                  setNewMembership({ ...newMembership, discountValue: value })
                }
                showSearch
                optionFilterProp="label"
                listHeight={256}
                options={discountOptions}
              />
            </Col>
            {/* <Col span={12}>
              <div style={{ marginBottom: 8, color: "#666" }}>End date</div>
              <DatePicker
                placeholder="Select end date"
                style={{ width: "100%" }}
                value={newMembership.discountEndDate}
                onChange={(date) =>
                  setNewMembership({ ...newMembership, discountEndDate: date })
                }
              />
            </Col> */}
            <Col span={12}>
              <div style={{ marginBottom: 8, color: "#666" }}>
                Discount duration
              </div>
              <InputNumber
                min={1}
                placeholder="Months"
                style={{ width: "100%" }}
                value={newMembership.numberOfMonths ?? undefined}
                onChange={(value) =>
                  setNewMembership({ ...newMembership, numberOfMonths: value })
                }
              />
            </Col>
          </Row>

          {/* <Row gutter={16} style={{ marginTop: 16 }}>
            <Col span={12}>
              <div style={{ marginBottom: 8, color: "#666" }}>
                Number of months
              </div>
              <InputNumber
                min={1}
                placeholder="Number of months"
                style={{ width: "100%" }}
                value={newMembership.numberOfMonths ?? undefined}
                onChange={(value) =>
                  setNewMembership({ ...newMembership, numberOfMonths: value })
                }
              />
            </Col>
          </Row> */}

          <Row justify="end" style={{ marginTop: 20 }} gutter={12}>
            <Col>
              <Button onClick={handleCancelAdd}>Cancel</Button>
            </Col>
            <Col>
              <Button type="primary" onClick={handleAddMembership}>
                Add User
              </Button>
            </Col>
          </Row>
        </Card>
      )}
    </div>
  );
}
